import React, { useState, useEffect } from 'react';
import { OptimizedImage } from '@/components/OptimizedImage';
import { useAuth } from '@/contexts/AuthContext';
import { UnifiedHeroSection } from '@/components/UnifiedHeroSection';
import { FAQSection, faqStructuredData } from '@/components/FAQSection';
import { MilkyWayCalendar } from '@/components/MilkyWayCalendar';
import { SEOHead } from '@/components/SEOHead';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { sanitizeDashboardReturnPath } from '@/utils/dashboardNavigation';
import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNewsletterGate } from '@/hooks/useNewsletterGate';
import { NewsletterGateModal } from '@/components/NewsletterGateModal';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { CreateAnnualCalendarModal } from '@/components/CreateAnnualCalendarModal';
import { Button } from '@/components/ui/button';
import { FileText, Loader2, Moon, ArrowRight, Target, Camera } from 'lucide-react';
import { TrackerExplainerSection } from '@/components/TrackerExplainerSection';

interface LocationData {
  name: string;
  latitude: number;
  longitude: number;
}

const Index: React.FC = () => {
  const { user, loading, isSubscribed, isRecoveryMode } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  // Check for shared location in URL params
  const sharedLocation = searchParams.get('location');
  const sharedLat = searchParams.get('lat');
  const sharedLon = searchParams.get('lon');
  const sharedMonth = searchParams.get('month');
  const sharedYear = searchParams.get('year');

  // Parse initial location from URL if present
  const initialLocation: LocationData | null =
    sharedLocation && sharedLat && sharedLon
      ? {
          name: decodeURIComponent(sharedLocation),
          latitude: parseFloat(sharedLat),
          longitude: parseFloat(sharedLon)
        }
      : null;

  // Parse initial date from URL if present
  const initialMonth = sharedMonth ? parseInt(sharedMonth, 10) : new Date().getMonth();
  const initialYear = sharedYear ? parseInt(sharedYear, 10) : new Date().getFullYear();

  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(initialLocation);
  const [selectedDate, setSelectedDate] = useState({
    month: initialMonth,
    year: initialYear
  });
  const [showAnnualCalendarModal, setShowAnnualCalendarModal] = useState(false);
  const [isCreatingCalendar, setIsCreatingCalendar] = useState(false);

  const {
    showGate,
    incrementViewCount,
    markAsSubscribed,
    closeGate,
    remainingViews
  } = useNewsletterGate();

  const handleCreateAnnualCalendar = async () => {
    if (!selectedLocation) return;

    // For premium users with email, create directly
    if (user && isSubscribed) {
      setIsCreatingCalendar(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userEmail = session?.user?.email;

        if (userEmail) {
          const { data, error } = await supabase.functions.invoke('create-shared-calendar', {
            body: {
              email: userEmail,
              location_name: selectedLocation.name,
              latitude: selectedLocation.latitude,
              longitude: selectedLocation.longitude,
              year: selectedDate.year,
              user_id: user.id,
              is_premium_user: true,
              subscribe_newsletter: false
            }
          });

          if (error) throw error;

          toast({
            title: 'Annual Calendar Created!',
            description: 'Redirecting to your calendar...',
          });

          navigate(`/calendar/${data.slug}`);
          return;
        }
      } catch (error) {
        console.error('Error creating calendar:', error);
        toast({
          title: 'Error',
          description: 'Failed to create calendar. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setIsCreatingCalendar(false);
      }
    }

    // For non-premium users, show the modal
    setShowAnnualCalendarModal(true);
  };

  // Show welcome message for users who just completed premium signup
  useEffect(() => {
    const welcome = searchParams.get('welcome');
    if (welcome === 'true') {
      toast({
        title: 'Premium Account Activated!',
        description: 'Your account has been confirmed. Please sign in to access your premium features.',
        duration: 6000,
      });
      // Clean up the URL parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('welcome');
      navigate('?' + newSearchParams.toString(), { replace: true });
    }
  }, [searchParams, toast, navigate]);

  // Redirect authenticated users to dashboard (but not during password recovery or when viewing shared plan)
  const hasSharedPlan = !!(sharedLocation && sharedLat && sharedLon);

  useEffect(() => {
    if (user && !loading && !isRecoveryMode && !hasSharedPlan) {
      const target =
        sanitizeDashboardReturnPath(searchParams.get('next')) ?? '/dashboard';
      navigate(target);
    }
  }, [user, loading, navigate, isRecoveryMode, hasSharedPlan, searchParams]);

  // Only return null AFTER we've confirmed the user IS authenticated and redirecting
  // During loading or SSG, render the full page so SEO content is visible
  if (!loading && user && !isRecoveryMode && !hasSharedPlan) {
    return null;
  }

  // Enhanced structured data for the homepage
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Milky Way Planner",
    "description": "Plan your astrophotography sessions with precise astronomical data for optimal Milky Way visibility. Get galactic core timing, moon phases, and visibility ratings for any location.",
    "url": "https://www.milkywayplanner.com",
    "applicationCategory": "PhotographyApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "creator": {
      "@type": "Organization",
      "name": "Milky Way Planner",
      "url": "https://www.milkywayplanner.com",
      "logo": "https://www.milkywayplanner.com/lovable-uploads/48a0f3cd-b38c-4efd-956b-0555da8f4f45.png"
    },
    "featureList": [
      "Galactic core timing calculations",
      "Moon photography planning with supermoon tracking",
      "Golden hour and blue hour calculations",
      "Worldwide location support",
      "Visibility ratings",
      "Astronomical data integration"
    ],
    "screenshot": "https://www.milkywayplanner.com/images/og-default.png"
  };

  // Generate dynamic OG image URL for shared plans
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dynamicOgImageUrl = hasSharedPlan && initialLocation
    ? `${SUPABASE_URL}/functions/v1/generate-og-image?location=${encodeURIComponent(initialLocation.name)}&lat=${initialLocation.latitude}&lon=${initialLocation.longitude}&month=${initialMonth}&year=${initialYear}`
    : undefined;

  const seoTitle = hasSharedPlan && initialLocation
    ? `Milky Way Plan - ${initialLocation.name.split(',')[0]}, ${monthNames[initialMonth]} ${initialYear}`
    : "Milky Way Planner — Free Astrophotography Planning Tool";

  const seoDescription = hasSharedPlan && initialLocation
    ? `Best nights for Milky Way photography in ${initialLocation.name.split(',')[0]} for ${monthNames[initialMonth]} ${initialYear}. See galactic core visibility, moon phases, and ratings.`
    : "Milky Way tracker with a nightly visibility forecast: each night rated 1 to 10 for your location by moon brightness when the core is up in full darkness. Free.";

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords="milky way planner, milky way calendar, astrophotography planner, milky way visibility, galactic core rise time, best time to see milky way, milky way season 2026, moon phase calendar, night sky planner, dark sky locations, astrophotography calendar, when to see milky way"
        canonicalUrl="/"
        structuredData={[homeStructuredData, faqStructuredData]}
        ogImageAbsolute={dynamicOgImageUrl}
      />
      <div className="min-h-screen bg-background">

        {/* 1. Hero — full-width edge-to-edge */}
        <UnifiedHeroSection
          onLocationChange={(location) => {
            if (location) {
              navigate(`/planner?name=${encodeURIComponent(location.name)}&lat=${location.latitude}&lon=${location.longitude}`);
            } else {
              setSelectedLocation(null);
            }
          }}
          onDateChange={setSelectedDate}
          selectedDate={selectedDate}
          initialLocation={initialLocation}
        />

        {/* 2. Calendar results */}
        {selectedLocation && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-16 mb-8">
            <div className="mb-6 flex justify-center">
              <Button
                onClick={handleCreateAnnualCalendar}
                disabled={isCreatingCalendar}
                className="px-8 py-6 text-lg rounded-[4px] transition-all"
              >
                {isCreatingCalendar ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <FileText className="mr-2 h-5 w-5" />
                )}
                {isCreatingCalendar ? 'Creating...' : 'Create Annual Calendar'}
              </Button>
            </div>

            <MilkyWayCalendar
              location={selectedLocation}
              month={selectedDate.month}
              year={selectedDate.year}
              onViewChange={incrementViewCount}
              forceShowHeader={hasSharedPlan}
            />

            {/* How to read — visual legend */}
            <div className="max-w-lg mx-auto mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span><span className="font-medium text-foreground">7–10</span> Ideal</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <span><span className="font-medium text-foreground">4–6</span> Some moon</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                <span><span className="font-medium text-foreground">0–3</span> Poor</span>
              </div>
            </div>
          </div>
        )}

        {/* 3. Three-path value prop strip — aligned with hero content */}
        {!selectedLocation && (
          <section className="border-b border-border">
            <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-10 sm:py-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Plan Your Shoot */}
                <Link to="/planner" className="group p-6 sm:p-8 rounded-xl border border-border hover:bg-muted/50 transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center mb-4">
                    <Target className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Plan Your Shoot</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    Free Milky Way calendar with nightly visibility ratings, galactic core timing, and moon phases for any location.
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-foreground group-hover:text-gold-dark transition-colors">
                    Open Planner <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>

                {/* Find a Workshop */}
                <Link to="/workshops" className="group p-6 sm:p-8 rounded-xl border border-border hover:bg-muted/50 transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center mb-4">
                    <Camera className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Find a Workshop</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    Astrophotography workshops at dark sky locations. Hands-on instruction from experienced photographers.
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-foreground group-hover:text-gold-dark transition-colors">
                    Browse Workshops <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>

                {/* Go Premium register card */}
                <Link
                  to="/register"
                  id="premium"
                  className="group block scroll-mt-28 bg-[#0f1923] hover:bg-[#1a2b3d] p-6 sm:p-8 rounded-xl ring-1 ring-gold/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
                >
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                    <Moon className="w-5 h-5 text-gold-light" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Go Premium</h3>
                  <p className="text-sm text-white/65 leading-relaxed mb-4">
                    Moon Planner, Trip Planner, unlimited locations, PDF/Excel exports, everything for $25/year.
                  </p>
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold hover:bg-gold-light group-hover:bg-gold-light text-slate-950 font-semibold rounded-[4px] transition-colors text-sm">
                    Start Premium, $25/yr <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* 4. Single screenshot */}
        {!selectedLocation && (
          <section className="py-14 sm:py-18 px-4 sm:px-6">
            <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="overflow-hidden rounded-[4px] border border-border shadow-lg">
                <OptimizedImage
                  src="/lovable-uploads/screenshot-milkyway-calendar.webp"
                  alt="Milky Way visibility calendar showing nightly ratings, galactic core timing, and moon phases for Moab, Utah"
                  width={640}
                  className="w-full"
                />
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">See every night rated at a glance</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Enter any location and get a complete month of Milky Way visibility data. Galactic core timing, moon phases, sunrise/sunset — everything you need to pick the perfect night.
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="text-gold-foreground font-medium">Free</span> — no account required.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* 5. Premium + Workshops side-by-side */}
        <section className="bg-slate-950 py-14 sm:py-18">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className={`grid gap-6 ${!user || !isSubscribed ? 'md:grid-cols-2' : ''}`}>
              {/* Premium card — hidden for subscribers */}
              {(!user || !isSubscribed) && (
                <div className="rounded-xl p-6 sm:p-8 bg-gold/8 border border-gold/25">
                  <span className="text-[#c9922a] uppercase text-xs font-semibold tracking-[0.14em] mb-3 block">Premium</span>
                  <h3 className="text-xl font-bold text-white mb-1">Unlock the full toolkit</h3>
                  <div className="flex items-baseline gap-2 mb-5">
                    <span className="text-3xl font-bold text-white font-serif">$25</span>
                    <span className="text-white/50">/year</span>
                    <span className="text-xs text-white/35 ml-1">($2.08/mo)</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {[
                      'Moon Planner with supermoon tracking',
                      'Multi-stop Trip Planner',
                      'Unlimited saved locations',
                      'PDF, Excel & calendar exports',
                      'Full yearly planning view',
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-white/75">
                        <span className="text-gold mt-0.5 shrink-0">✦</span>{f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-light text-slate-950 font-semibold rounded-[4px] transition-colors text-sm"
                  >
                    Start Premium Account <ArrowRight className="w-4 h-4" />
                  </Link>
                  <p className="text-xs text-white/30 mt-3">Cancel anytime.</p>
                </div>
              )}

              {/* Workshops card — always visible */}
              <div className={`rounded-xl p-6 sm:p-8 bg-white/4 border border-white/10 ${user && isSubscribed ? 'max-w-xl mx-auto' : ''}`}>
                <span className="text-[#c9922a] uppercase text-xs font-semibold tracking-[0.14em] mb-3 block">Workshops</span>
                <h3 className="text-xl font-bold text-white mb-2">Learn from the pros</h3>
                <p className="text-sm text-white/60 leading-relaxed mb-5">
                  Find astrophotography workshops at dark sky locations worldwide, or list your own to reach thousands of photographers.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <Link
                    to="/workshops"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-gold text-gold hover:bg-gold hover:text-slate-950 font-semibold rounded-[4px] transition-colors text-sm"
                  >
                    <Camera className="w-4 h-4" />
                    Browse Workshops
                  </Link>
                  <Link
                    to="/workshops/instructors"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/20 text-white/60 hover:text-white hover:border-white/40 font-medium rounded-[4px] transition-colors text-sm"
                  >
                    Instructors <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>

                <div className="pt-5 border-t border-white/10">
                  <p className="text-xs text-white/40 uppercase tracking-wider font-semibold mb-2">For Workshop Leaders</p>
                  <p className="text-sm text-white/55 leading-relaxed mb-3">
                    List your workshops where photographers are actively planning their shoots. $200/year, unlimited listings.
                  </p>
                  <Link
                    to="/workshops/list-your-workshop"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/15 text-white/50 hover:text-white/80 hover:border-white/30 font-medium rounded-[4px] transition-colors text-sm"
                  >
                    List Your Workshop <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. How this tracker works — after the cards, before the founder story */}
        <TrackerExplainerSection />

        {/* 7. Founder story */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-start">
            <div className="overflow-hidden rounded-[4px] border-2 border-foreground/15 shadow-xl">
              <OptimizedImage
                src="/lovable-uploads/429f23c9-b522-430c-8d9c-f8e47a1ef3eb.webp"
                alt="Milky Way and comet over Lake Superior"
                width={640}
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <div className="space-y-5">
              <h2 className="text-2xl text-foreground tracking-[-0.02em]">Why I built this</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                If you've ever driven hours to a dark-sky location only to find the galactic core below the horizon, or washed out by a moon you didn't account for, you know why planning matters.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Milky Way photography depends on three things at once: core above the horizon, moon down or near new, and enough darkness. Miss any one and you're going home empty-handed.
              </p>
              <blockquote className="text-lg text-muted-foreground italic border-l-2 border-gold pl-4">
                I built this because I got tired of cross-referencing multiple apps to figure out whether a trip was worth planning. So I built one tool that does it all: galactic core timing, moon phases, visibility ratings, everything you need to decide if a night is worth the drive.
              </blockquote>
              <div className="pt-4">
                <p className="text-foreground font-semibold">
                  Eric D. Brown, D.Sc.<br />
                  <span className="text-primary">Photographer</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Annual Calendar Modal */}
        {selectedLocation && (
          <CreateAnnualCalendarModal
            location={selectedLocation}
            selectedYear={selectedDate.year}
            isOpen={showAnnualCalendarModal}
            onClose={() => setShowAnnualCalendarModal(false)}
          />
        )}

        {/* Newsletter Gate Modal */}
        <NewsletterGateModal
          open={showGate}
          onClose={closeGate}
          onSuccess={markAsSubscribed}
        />

        {/* Newsletter */}
        <NewsletterSignup />

        {/* FAQ */}
        <div className="section-alt py-20 sm:py-24 px-4 sm:px-6">
          <FAQSection />
        </div>

      </div>
    </>
  );
};

export default Index;
